/**
 * voice-v8 — Werkzeugkarten: Dateien: Aufgaben-Dateien, Download, gelesene Datei, PDF, Drive-Suche, geöffneter Link.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, baueKarte, kartenTitel, kuerzeListe, leerHinweis, liste, listenEintrag, weitereHinweis, wertZeile } from "../karte.js";
import { buildCloseButton } from "../sheet-ui.js";
import { icon } from "../icons.js";
import { isNativePlatform } from "../native-voice.js";
import { kannVorgelesenWerden } from "../datei-art.js";
import { fmtDateShort, trimText, unwrapResult } from "./hilfe.js";

// Pre.801 (#71): task_files — erzeugte Dateien eines Hintergrund-Tasks mit
// direktem Öffnen-Knopf (vorher: User suchte vergeblich in der Mediathek).
interface TaskFilesShape {
  project?: string | null;
  files?: Array<{ title?: string; url?: string }>;
}

export function buildTaskFilesCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as TaskFilesShape;
  const el = baueKarte("task-files");
  el.appendChild(kartenTitel("Dateien"));
  const files = Array.isArray(r.files) ? r.files : [];
  if (files.length === 0) {
    leerHinweis(el, "Keine Dateien.");
    return el;
  }
  if (r.project) {
    const where = document.createElement("div");
    where.className = "tool-browser-detail";
    where.textContent = `Gespeichert in Projekte → ${r.project} → Dateien`;
    el.appendChild(where);
  }
  const gekuerzt = kuerzeListe(files, 6);
  for (const f of gekuerzt.sichtbar) {
    if (!f.url || !f.title) continue;
    const row = document.createElement("div");
    row.className = "tool-actions";
    const a = document.createElement("a");
    a.className = "tool-action-btn primary";
    a.href = f.url;
    a.rel = "noopener noreferrer";
    a.appendChild(icon("download", { size: 14 }));
    a.appendChild(document.createTextNode(` ${trimText(f.title, 60)}`));
    // Pre.802 (#77): Projekt-Dateien im APP-EIGENEN Viewer öffnen — ein
    // neuer Tab überlagerte die PWA am Handy unbedienbar. href bleibt als
    // Fallback (Long-Press / "in neuem Tab öffnen").
    const m = /\/api\/projects\/([^/]+)\/files\/([^/]+)\/raw/.exec(f.url);
    if (m) {
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        void import("../project-file-viewer.js").then(
          ({ openProjectFileViewer }) =>
            openProjectFileViewer(
              decodeURIComponent(m[1]),
              decodeURIComponent(m[2]),
            ),
        );
      });
    } else {
      a.target = "_blank";
    }
    row.appendChild(a);
    el.appendChild(row);
  }
  weitereHinweis(el, gekuerzt.rest);
  return el;
}

// Ticket 212: attach_file / make_pdf — eine bekannte Datei als Ansehen-/
// Herunterladen-Card direkt im Chat (kein Umweg ueber den Projekt-Tab).
interface FileDownloadShape {
  project?: string | null;
  title?: string;
  url?: string;
  mime?: string;
  sizeBytes?: number;
  size?: number;
  warning?: string;
  files?: Array<{
    title?: string;
    url?: string;
    mime?: string;
    sizeBytes?: number;
    downloadName?: string;
  }>;
}

export function buildFileDownloadCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as FileDownloadShape;
  let files = Array.isArray(r.files) ? r.files.slice() : [];
  if (files.length === 0 && typeof r.url === "string" && r.url) {
    // make_pdf-Form: { url, title, size }
    files = [
      {
        title: r.title || "Datei",
        url: r.url,
        mime: r.mime,
        sizeBytes: r.sizeBytes ?? r.size,
      },
    ];
  }
  const el = baueKarte("file-download");
  el.appendChild(kartenTitel(files.length > 1 ? "Dateien" : "Datei"));
  if (files.length === 0) {
    leerHinweis(el, "Keine Datei.");
    return el;
  }
  if (r.project) {
    const where = document.createElement("div");
    where.className = "tool-browser-detail";
    where.textContent = `Aus Projekt: ${trimText(String(r.project), 40)}`;
    el.appendChild(where);
  }
  for (const f of files.slice(0, 6)) {
    if (!f.url) continue;
    const name = trimText(f.title || "Datei", 60);
    const sizeStr =
      typeof f.sizeBytes === "number" && f.sizeBytes > 0
        ? `  \u00b7  ${Math.max(1, Math.round(f.sizeBytes / 1024))} KB`
        : "";
    const ttl = document.createElement("div");
    ttl.className = "tool-browser-detail";
    ttl.textContent = name + sizeStr;
    el.appendChild(ttl);

    const row = document.createElement("div");
    row.className = "tool-actions";

    // Ansehen
    const view = document.createElement("a");
    view.className = "tool-action-btn primary";
    view.href = f.url;
    view.rel = "noopener noreferrer";
    view.appendChild(icon("external", { size: 14 }));
    view.appendChild(document.createTextNode(" Ansehen"));
    const m = /\/api\/projects\/([^/]+)\/files\/([^/]+)\/raw/.exec(f.url);
    const oeffneBetrachter = (vorlesen: boolean): void => {
      if (!m) return;
      void import("../project-file-viewer.js").then(
        ({ openProjectFileViewer }) =>
          openProjectFileViewer(
            decodeURIComponent(m[1] ?? ""),
            decodeURIComponent(m[2] ?? ""),
            { vorlesen },
          ),
      );
    };
    if (m) {
      view.addEventListener("click", (ev) => {
        ev.preventDefault();
        oeffneBetrachter(false);
      });
    } else {
      view.target = "_blank";
    }
    row.appendChild(view);

    /*
     * „Anhören" — Ticket 2395. (19.09.2026)
     *
     * Der Spieler sitzt seit pre.974 im Datei-Betrachter; es fehlte nur der
     * erkennbare Weg dorthin. „Ansehen" mit einem Öffnen-Symbol sagt einem,
     * der hören will, nicht, dass er dort richtig ist.
     *
     * ⚠️ Nur bei Textdateien und nur bei Projektdateien: ohne Betrachter
     *    (kein `m`) gäbe es nichts zu öffnen, und bei einem PDF oder Bild
     *    hätte der Spieler nichts vorzulesen.
     */
    if (m && kannVorgelesenWerden({ title: f.title, mime: f.mime })) {
      const hoer = document.createElement("button");
      hoer.type = "button";
      hoer.className = "tool-action-btn";
      hoer.appendChild(icon("play", { size: 14 }));
      hoer.appendChild(document.createTextNode(" Anhören"));
      hoer.addEventListener("click", () => oeffneBetrachter(true));
      row.appendChild(hoer);
    }

    /*
     * „Herunterladen" — NUR im Browser. (Ticket 2396, 20.09.2026)
     *
     * Martin: *„Statt die Datei innerhalb der nativen App zu oeffnen,
     * oeffnet sich direkt die Datei, und die native App tritt dabei
     * komplett in den Hintergrund."*
     *
     * Ein nacktes `<a download>` erzeugt im Browser einen Speichern-Vorgang.
     * In der nativen WebView gibt es dafuer keinen Speichern-Dialog: iOS
     * reicht die Datei an die Systemansicht weiter und schiebt die App weg.
     *
     * ⚠️ Ein Knopf, der einen aus der App wirft, ist schlechter als keiner.
     *    In der App bleiben „Ansehen" und „Anhoeren" — beide oeffnen den
     *    eigenen Betrachter und bleiben drin.
     *
     * ⚠️ Der Funktionsverlust ist GEMESSEN, nicht geschaetzt: im
     *    nginx-Protokoll wurde dieser Weg in vierzehn Tagen ZWEIMAL benutzt
     *    (10.09. ueber einen Player, 19.09. vollstaendig). Das Speichern in
     *    der nativen App braucht eine native Bruecke und einen
     *    TestFlight-Bau; bis dahin ist Wegnehmen die ehrliche Variante.
     */
    if (!isNativePlatform()) {
      const dl = document.createElement("a");
      dl.className = "tool-action-btn";
      dl.href = f.url;
      dl.setAttribute("download", f.downloadName || name);
      dl.rel = "noopener noreferrer";
      dl.appendChild(icon("download", { size: 14 }));
      dl.appendChild(document.createTextNode(" Herunterladen"));
      row.appendChild(dl);
    }

    el.appendChild(row);
  }
  if (r.warning) leerHinweis(el, trimText(String(r.warning), 160));
  return el;
}

// --- Drive ---------------------------------------------------------------

interface DriveItem {
  id?: string;
  name?: string;
  mimeType?: string;
  webViewLink?: string;
  modifiedTime?: string;
}

const MIME_LABEL: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.google-apps.spreadsheet": "Tabelle",
  "application/vnd.google-apps.document": "Dokument",
  "application/vnd.google-apps.presentation": "Slides",
  "application/vnd.google-apps.folder": "Ordner",
  "image/jpeg": "Bild",
  "image/png": "Bild",
  "video/mp4": "Video",
};

export function buildDriveSearchCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("drive-search");
  const items = (r.items as DriveItem[] | undefined) ?? [];
  const count = (r.count as number | undefined) ?? items.length;
  el.appendChild(kartenTitel(`Drive · ${count} Treffer`));
  if (items.length === 0) {
    leerHinweis(el, "Keine Dateien gefunden.");
    return el;
  }
  const ul = liste(el);
  const gekuerzt = kuerzeListe(items);
  for (const it of gekuerzt.sichtbar) {
    const meta = it.modifiedTime ? fmtDateShort(it.modifiedTime) : undefined;
    const type = MIME_LABEL[it.mimeType ?? ""] ?? "Datei";
    ul.appendChild(
      listenEintrag(it.name ?? "(unbenannt)", type, meta, it.webViewLink),
    );
  }
  weitereHinweis(el, gekuerzt.rest);
  return el;
}

// --- make_pdf ------------------------------------------------------------

function openPdfInModal(url: string, title: string): void {
  // Pre.335: PDF-Vorschau im In-App-Modal statt externen Tab/App-Wechsel.
  // Martin-Beschwerde 2026-05-17: "Wenn ich auf PDF klicke, soll Modal
  // öffnen, sodass ich zurück zur Chatübersicht komme — nicht das System
  // schließen müssen." iOS/PWA verließ vorher die App komplett.
  document.getElementById("pdf-preview-modal")?.remove();
  const overlay = document.createElement("div");
  overlay.id = "pdf-preview-modal";
  overlay.className = "modal-overlay";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) overlay.remove();
  });
  const panel = document.createElement("div");
  panel.className = "modal-panel modal-panel--pdf";

  const closeBtn = buildCloseButton(() => overlay.remove());
  closeBtn.classList.add("close-btn--corner");
  panel.appendChild(closeBtn);

  const heading = document.createElement("h2");
  heading.className = "modal-title";
  heading.textContent = title || "PDF-Vorschau";
  panel.appendChild(heading);

  const frame = document.createElement("iframe");
  frame.className = "modal-pdf-frame";
  frame.src = url;
  frame.setAttribute("title", title || "PDF");
  panel.appendChild(frame);

  const actions = document.createElement("div");
  actions.className = "modal-actions";
  const dl = document.createElement("a");
  dl.href = url;
  dl.target = "_blank";
  dl.rel = "noopener noreferrer";
  dl.className = "modal-btn";
  dl.textContent = "In neuem Tab öffnen";
  actions.appendChild(dl);
  const closeAction = document.createElement("button");
  closeAction.type = "button";
  closeAction.className = "modal-btn modal-btn--secondary";
  closeAction.textContent = "Zurück zum Chat";
  closeAction.addEventListener("click", () => overlay.remove());
  actions.appendChild(closeAction);
  panel.appendChild(actions);

  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}

export function buildMakePdfCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("make-pdf");
  el.appendChild(kartenTitel("PDF erstellt"));
  if (r.title) wertZeile(el, "Titel", String(r.title));
  if (typeof r.page_count === "number")
    wertZeile(el, "Seiten", String(r.page_count));
  if (typeof r.size === "number") {
    const kb = Math.round(r.size / 1024);
    wertZeile(el, "Größe", `${kb} kB`);
  }
  if (r.url) {
    // Pre.335: PDF-Button öffnet Modal mit iframe statt externen Tab.
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tool-list-link tool-list-link--btn";
    const lbl = document.createElement("span");
    lbl.textContent = "PDF öffnen";
    btn.appendChild(lbl);
    const titleStr = r.title ? String(r.title) : "PDF-Vorschau";
    const urlStr = String(r.url);
    btn.addEventListener("click", () => openPdfInModal(urlStr, titleStr));
    el.appendChild(btn);
  }
  return el;
}

// --- read_file -----------------------------------------------------------

export function buildReadFileCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("read-file");
  el.appendChild(
    kartenTitel(`Datei gelesen${r.type ? " · " + String(r.type) : ""}`),
  );
  if (r.filename) wertZeile(el, "Datei", String(r.filename));
  if (r.mime) wertZeile(el, "Typ", String(r.mime));
  const content = (r.content ?? r.transcript) as string | undefined;
  if (content) {
    // 15.10.2026: ein Anriss, kein Dump — die Datei liest das Modell, nicht der Schirm (Karten-Größe: 400).
    el.appendChild(absatz(trimText(content, 400)));
  }
  if (r.truncated) leerHinweis(el, "(Inhalt gekürzt)");
  return el;
}

// --- open_url ------------------------------------------------------------

export function buildOpenUrlCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("open-url");
  el.appendChild(kartenTitel("Link geöffnet"));
  const url = (r.url ?? r.openedUrl) as string | undefined;
  if (url) {
    const a = document.createElement("a");
    a.className = "tool-list-link";
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = url.length > 60 ? url.slice(0, 60) + "…" : url;
    el.appendChild(a);
  }
  return el;
}
